import type { AxiosError, AxiosResponse } from "axios";
import { getCookie, isDebugAPI, logErrorToSentry } from "@litres/utils";
import type {
  IGlobalApiParams,
  IReduxRequestNode,
  ICat3Response,
  IResponse,
  IResponseErrorDetails,
} from "@litres-libs/types";
import axios from "../../instance/axios";
import type { ICat3Config } from "./../config/cat3";
import { cat3Config } from "./../config/cat3";
import { ApiError, ApiType } from "../../types/api";

/**
 * Провайдер запросов в каталит3.
 * Создает синглтон, чтобы все запросы шли через 1 точку
 */
export class Catalit3PromiseProvider<TParams, TPayload> {
  private constructor(config: ICat3Config, initialSID: string | undefined) {
    this.SID = initialSID || undefined;
    this.baseURL = config.baseURL || "DEFAULT_BASE_URL";
  }

  private SID: string | undefined;

  private baseURL: string;

  private static instance: Catalit3PromiseProvider<any, any>;

  /**
   * Метод, возвращающий единичный экземпляр синглтона
   * @returns Возвращает провайдер каталита для вызова запросов
   */
  public static getInstance<TParams, TPayload>(): Catalit3PromiseProvider<
    TParams,
    TPayload
  > {
    if (!Catalit3PromiseProvider.instance) {
      // Передачу конфига не делаем, рассчитываем что мы работаем с одним каталитом по одному конфигу
      // По умолчанию берем SID из куки, но в дальнейшем дадим делать запрос от сида в параметрах запроса
      const initialSID = getCookie("SID");
      Catalit3PromiseProvider.instance = new Catalit3PromiseProvider(
        cat3Config,
        initialSID
      );
    }

    return Catalit3PromiseProvider.instance;
  }

  /**
   * Основной метод вызова запроса
   * @param params - стандартные передаваемые параметры для всех запросов в любые наши API
   * @returns {Promise} - Возвращает пропис запроса каталита
   */
  public async makeAxiosRequest(
    params: IGlobalApiParams<TParams>
  ): Promise<IReduxRequestNode<TParams, TPayload>> {
    const createHeaders = () => {
      const headers = {} as {
        Authorization: string;
      };

      if (this.SID) {
        headers["Authorization"] = `${this.SID}`;
      }

      return headers;
    };

    const url =
      params.url && params.url !== "/"
        ? this.baseURL + params.url
        : this.baseURL;
    const method = params.request ? params.request.method : "POST";
    const data = params.request ? params.request.data : {};
    const _params = params.request ? params.request.params : {};
    const headers = createHeaders();

    // Делаем запрос
    const res = axios({
      url,
      method,
      data,
      params: _params,
      // TODO: Потребуется добавить авторизацию через заголовок Bearer Token
      // Пока установим авторизационный заголовок, обсуждали что в будущем будет работать так
      headers,
      timeout: 40000,
    })
      .then((response: AxiosResponse<ICat3Response<TPayload>>) => {
        if (isDebugAPI) {
          console.info(
            `%cRES: ${params.provider.id}`,
            "background: #abff5f; padding: 2px 12px; color: #848484",
            response.data,
            params
          );
        }

        if (params.onSuccess) {
          params.onSuccess({
            request: params,
            response: response.data,
          });
        }

        delete params.onError;
        delete params.onSuccess;

        // В случае успешного ответа вернем ответ в fullfill
        return {
          request: params,
          response: response.data,
        };
      })
      .catch((error: AxiosError<IResponse>) => {
        if (error.response?.status === 500) {
          logErrorToSentry(new ApiError("API Error (500)"), {
            contextProperties: [
              "REQUEST_PARAMS",
              {
                url,
                method,
                params: JSON.stringify(_params),
                headers,
                data: JSON.stringify(data),
              },
            ],
            tags: {
              api_name: ApiType.Cat3,
            },
          });
        }

        if (isDebugAPI) {
          console.error(
            `%cERR: ${params.provider.id}`,
            "background: #f55e5e; padding: 2px 12px; color: #fff",
            error
          );
        }

        if (params.onError) {
          params.onError({
            request: params,
            error: {
              status: error.response?.status,
              type: (error.response?.data.error as IResponseErrorDetails).type,
              title: (error.response?.data.error as IResponseErrorDetails)
                .title,
            },
          });
        }

        delete params.onError;
        delete params.onSuccess;

        return {
          request: params,
          error: {
            status: error.response?.status,
            type: (error.response?.data.error as IResponseErrorDetails).type,
            title: (error.response?.data.error as IResponseErrorDetails).title,
          },
        };
      });

    return res as Promise<IReduxRequestNode<TParams, TPayload>>;
  }
}
